/**
 * Détections de feu par l'API FIRMS, via le relais serveur.
 *
 * Source à préférer au balayage d'images, parce qu'elle apporte ce que l'image
 * PNG ne contient pas : un indice de confiance, la puissance radiative et
 * l'heure réelle du passage satellite. C'est ce qui permet de distinguer un
 * brûlage agricole d'un front de flammes.
 *
 * Le relais vit dans `functions/api/fires.ts` et n'existe que sur Cloudflare
 * Pages. Ailleurs, l'appel échoue et l'application retombe sur le balayage
 * d'images. Le même build fonctionne donc sur les deux hébergements, et
 * s'améliore de lui-même une fois migré.
 */
import { FOOTPRINT_DEFAULT_KM, FOOTPRINT_MODIS_KM, FOOTPRINT_VIIRS_KM } from '../core/constants';
import type { FireDetection } from '../core/types';
import { getJson } from './http';

/**
 * Empreinte au sol du pixel, déduite du nom de la source.
 *
 * FIRMS ne renvoie pas la résolution : elle se lit dans le nom de
 * l'instrument. Une source inconnue prend la plus grossière des deux, pour ne
 * jamais dessiner un feu plus petit que ce qui a été mesuré.
 */
function footprintOf(source: string | undefined): number {
  if (source === undefined) return FOOTPRINT_DEFAULT_KM;
  if (source.includes('VIIRS')) return FOOTPRINT_VIIRS_KM;
  if (source.includes('MODIS')) return FOOTPRINT_MODIS_KM;
  return FOOTPRINT_DEFAULT_KM;
}

/**
 * Résolue contre `document.baseURI`, pas en chemin absolu.
 *
 * Le build produit des chemins relatifs pour rester compatible Capacitor : un
 * `/api/fires` en dur casserait dès que le site est servi depuis un
 * sous-répertoire.
 */
function endpoint(): string {
  return new URL('api/fires', document.baseURI).toString();
}

interface FirmsResponse {
  detections?: {
    lat?: number;
    lon?: number;
    confidence?: number;
    frp?: number | null;
    at?: number | null;
    source?: string;
  }[];
  dayRange?: number;
  fetchedAt?: number;
}

export interface FirmsResult {
  detections: FireDetection[];
  fetchedAt: number;
}

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Interroge le relais.
 *
 * Renvoie `null` dès que la source n'est pas exploitable, sans lever :
 * l'appelant doit pouvoir se rabattre sans avoir à distinguer un relais absent
 * d'un relais en panne. Dans les deux cas la conduite à tenir est la même.
 */
export async function fetchFirmsDetections(): Promise<FirmsResult | null> {
  try {
    const data = await getJson<FirmsResponse>(endpoint());
    const raw = data.detections;
    if (!Array.isArray(raw) || raw.length === 0) return null;

    const detections = raw.flatMap((entry): FireDetection[] => {
      const lat = finite(entry.lat);
      const lon = finite(entry.lon);
      if (lat === null || lon === null) return [];
      return [
        {
          lat,
          lon,
          confidence: finite(entry.confidence) ?? 50,
          frp: finite(entry.frp),
          at: finite(entry.at),
          footprintKm: footprintOf(
            typeof entry.source === 'string' ? entry.source : undefined,
          ),
        },
      ];
    });

    if (detections.length === 0) return null;
    return { detections, fetchedAt: finite(data.fetchedAt) ?? Date.now() };
  } catch {
    // Relais absent, clé manquante, ou FIRMS indisponible.
    return null;
  }
}
